import { useNavigate } from 'react-router';
import { DialogContent, DialogActions, TextField, Button, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { GenericDialog } from '../../components/GenericDialog';
import useLocales from '../../hooks/useLocales';
import { useDispatch } from '../../redux/store';
import { createOrganization } from '../../features/organization-context/api';
import { PATH_ORG } from '../../routes/paths';

interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required('org_switcher.name_required').max(255)
});

export default function CreateOrganizationDialog({ open, onClose }: CreateOrganizationDialogProps) {
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: { name: '' },
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        await dispatch(createOrganization(values.name.trim()));
        helpers.resetForm();
        onClose();
        navigate(PATH_ORG.overview);
      } catch (err: any) {
        const message = err?.response?.data?.message ?? translate('org_switcher.create_error');
        helpers.setStatus({ error: message });
      }
    }
  });

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    onClose();
  };

  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      title={translate('org_switcher.create_organization')}
      maxWidth="sm"
      fullWidth
      disableBackdropClick={formik.isSubmitting}
      disableEscapeKeyDown={formik.isSubmitting}
      disableCloseButton={formik.isSubmitting}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {formik.status?.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formik.status.error}
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            autoFocus
            id="name"
            name="name"
            label={translate('org_switcher.organization_name')}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={
              formik.touched.name && formik.errors.name ? translate(formik.errors.name) : undefined
            }
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" size="small" disabled={formik.isSubmitting}>
            {translate('common.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            size="small"
            loading={formik.isSubmitting}
            disabled={!formik.values.name.trim()}
          >
            {translate('org_switcher.create_organization')}
          </LoadingButton>
        </DialogActions>
      </form>
    </GenericDialog>
  );
}
